import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  AccountTree as AccountTreeIcon,
  Percent as PercentIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MoneyIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../hooks/useAuth';
import MultiLevelCommissionManagement from './MultiLevelCommissionManagement';
import { formatCommissionAmount, formatCurrency } from '../utils/formatters';
import { validateNumericInput } from '../utils/inputValidation';

// Hierarchical Downline Interfaces
interface HierarchicalDownlineDto {
  id: number;
  userId: number;
  username: string;
  fullName: string;
  email: string;
  parentUserId?: number;
  parentUsername?: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  availableForUplines: number;
  hierarchyLevel: number;
  canCreateDownlines: boolean;
  createdAt: string;
  isActive: boolean;
  notes?: string;
  myTakeFromThem: number;
  remainingForMe: number;
  operatorCommissions: HierarchicalOperatorCommissionDto[];
  downlines: HierarchicalDownlineDto[];
  totalDownlinesCount: number;
  directDownlinesCount: number;
  hasDownlines: boolean;
}

interface HierarchicalDownlineResponseDto {
  currentUserId: number;
  currentUsername: string;
  currentFullName: string;
  currentLevel: number;
  directDownlines: HierarchicalDownlineDto[];
  totalDownlinesCount: number;
  maxDepth: number;
}

// Edit Downline Commission Form Component
interface EditDownlineCommissionFormProps {
  downlineId: number;
  operatorId: number;
  operatorName: string;
  onSave: (downlineId: number, operatorId: number, maxAllowed: number, ownTake: number, notes?: string) => void;
  onCancel: () => void;
  currentData?: HierarchicalOperatorCommissionDto;
}

const EditDownlineCommissionForm: React.FC<EditDownlineCommissionFormProps> = ({
  downlineId,
  operatorId,
  operatorName,
  onSave,
  onCancel,
  currentData
}) => {
  const [maxAllowed, setMaxAllowed] = useState<number>(currentData?.maxAllowedPercentage || 0); // Already in RM
  const [ownTake, setOwnTake] = useState<number>(currentData?.ownTakePercentage || 0); // Already in RM
  const [notes, setNotes] = useState<string>(currentData?.notes || '');

  const handleSave = () => {
    onSave(downlineId, operatorId, maxAllowed, ownTake, notes);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Edit {operatorName} Commission for Downline
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="They Can Take (RM/set)"
          type="number"
          value={maxAllowed}
          onChange={(e) => {
            const value = Math.max(0, Math.min(0.6, parseFloat(e.target.value) || 0));
            setMaxAllowed(value);
            // Ensure own take doesn't exceed max allowed
            if (ownTake > value) {
              setOwnTake(value);
            }
          }}
          inputProps={{ min: 0, max: 0.6, step: 0.01 }}
          helperText="Maximum amount the downline can take (0-0.60 RM/set)"
          fullWidth
        />
        
        <TextField
          label="They Take (RM/set)"
          type="number"
          value={ownTake}
          onChange={(e) => {
            const value = Math.max(0, Math.min(maxAllowed, parseFloat(e.target.value) || 0));
            setOwnTake(value);
          }}
          inputProps={{ min: 0, max: maxAllowed, step: 0.01 }}
          helperText={`Current amount the downline takes (0-${maxAllowed.toFixed(2)} RM/set)`}
          fullWidth
        />
        
        <TextField
          label="I Can Take (RM/set)"
          type="number"
          value={(maxAllowed - ownTake).toFixed(1)}
          disabled
          helperText="Available percentage for you to take"
          fullWidth
        />
        
        <TextField
          label="Notes"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this commission setting"
          fullWidth
        />
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

// Interfaces based on the backend DTOs
interface HierarchicalCommission {
  id: number;
  userId: number;
  username: string;
  fullName: string;
  email: string;
  parentUserId?: number;
  parentUsername?: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  availableForUplines: number;
  hierarchyLevel: number;
  canCreateDownlines: boolean;
  createdAt: string;
  isActive: boolean;
  notes?: string;
  downlines: HierarchicalCommission[];
  uplineTakes: UplineTake[];
}

interface UplineTake {
  id: number;
  targetUserId: number;
  targetUsername: string;
  targetFullName: string;
  uplineUserId: number;
  uplineUsername: string;
  uplineFullName: string;
  takePercentage: number;
  priority: number;
  updatedAt: string;
  isActive: boolean;
}

interface CreateDownlineRequest {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  password: string;
  maxAllowedPercentage: number;
  initialTakePercentage: number;
  canCreateDownlines: boolean;
  notes?: string;
  operatorCommissions?: OperatorCommission[];
}

interface OperatorCommission {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  maxAllowedPercentage: number;
  initialTakePercentage: number;
  notes?: string;
}

interface HierarchicalOperatorCommissionDto {
  id: number;
  hierarchicalCommissionId: number;
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  notes?: string;
}

interface OperatorSetup {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  defaultOwnTakePercentage: number;
  defaultMaxAllowedPercentage: number;
  baseAllocationPercentage: number;
  isSetup: boolean;
}

interface TimeRestriction {
  canEdit: boolean;
  message: string;
  currentTime: string;
  allowedStartTime: string;
  allowedEndTime: string;
}

interface CommissionBreakdown {
  userId: number;
  username: string;
  fullName: string;
  saleAmount: number;
  adminAmount: number;
  userKeeps: number;
  uplineTakes: UplineTakeBreakdown[];
  remainingToAdmin: number;
}

interface UplineTakeBreakdown {
  uplineUsername: string;
  uplineFullName: string;
  hierarchyLevel: number;
  takePercentage: number;
  takeAmount: number;
  priority: number;
}

const HierarchicalCommissionManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const queryClient = useQueryClient();

  // State management
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditOwnTakeDialog, setShowEditOwnTakeDialog] = useState(false);
  const [showEditUplineTakeDialog, setShowEditUplineTakeDialog] = useState(false);
  const [showBreakdownDialog, setShowBreakdownDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedDownlineForTake, setSelectedDownlineForTake] = useState<HierarchicalCommission | null>(null);
  const [selectedBreakdownUser, setSelectedBreakdownUser] = useState<HierarchicalCommission | null>(null);
  const [operators, setOperators] = useState<OperatorSetup[]>([]);
  const [operatorCommissions, setOperatorCommissions] = useState<OperatorCommission[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [showOperatorCommissionDialog, setShowOperatorCommissionDialog] = useState(false);
  const [editingOperatorCommissions, setEditingOperatorCommissions] = useState<any[]>([]);
  const [downlineOperatorCommissions, setDownlineOperatorCommissions] = useState<Record<number, HierarchicalOperatorCommissionDto[]>>({});
  const [editingDownlineCommissions, setEditingDownlineCommissions] = useState<Record<number, Record<number, { maxAllowed: number; ownTake: number; notes?: string }>>>({});
  const [showEditDialog, setShowEditDialog] = useState<{ downlineId: number; operatorId: number; operatorName: string } | null>(null);
  
  // Hierarchical downline state
  const [expandedHierarchicalRows, setExpandedHierarchicalRows] = useState<Set<string>>(new Set());
  const [showHierarchicalView, setShowHierarchicalView] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState<CreateDownlineRequest>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    maxAllowedPercentage: 0.6, // Default 60%
    initialTakePercentage: 0, // Default 0%
    canCreateDownlines: true,
    notes: ''
  });

  const [editOwnTakeForm, setEditOwnTakeForm] = useState({
    ownTakePercentage: 0
  });

  const [editUplineTakeForm, setEditUplineTakeForm] = useState({
    targetUserId: 0,
    takePercentage: 0
  });

  // Temporary state for free typing in text fields
  const [editingOwnTakeValue, setEditingOwnTakeValue] = useState<string>('');
  const [editingUplineTakeValue, setEditingUplineTakeValue] = useState<string>('');

  // Check time restrictions
  const { data: timeRestriction } = useQuery<TimeRestriction>({
    queryKey: ['time-restriction'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/time-restriction`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to check time restriction');
      return response.json();
    },
    refetchInterval: 60000 // Check every minute
  });

  // Fetch my commission
  const { data: myCommission, isLoading: loadingMyCommission } = useQuery<HierarchicalCommission>({
    queryKey: ['my-hierarchical-commission'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-commission`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to fetch commission data');
      }
      return response.json();
    }
  });

  // Fetch my downlines
  const { data: myDownlines = [], isLoading: loadingDownlines } = useQuery<HierarchicalCommission[]>({
    queryKey: ['my-hierarchical-downlines'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-downlines`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch downlines');
      return response.json();
    },
    enabled: !!myCommission
  });

  // Fetch my upline takes
  const { data: myUplineTakes = [] } = useQuery<UplineTake[]>({
    queryKey: ['my-upline-takes'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-upline-takes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch upline takes');
      return response.json();
    },
    enabled: !!myCommission
  });

  // Fetch my operator commissions
  const { data: myOperatorCommissionsData = [], isLoading: loadingOperatorCommissions } = useQuery<any[]>({
    queryKey: ['my-operator-commissions'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-operator-commissions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch operator commissions');
      return response.json();
    },
    enabled: !!myCommission
  });

  // Hierarchical downlines query
  const { data: hierarchicalDownlines, isLoading: loadingHierarchicalDownlines } = useQuery<HierarchicalDownlineResponseDto>({
    queryKey: ['hierarchical-downlines'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/hierarchical-downlines?maxDepth=5&includeOperatorCommissions=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch hierarchical downlines');
      return response.json();
    },
    enabled: showHierarchicalView
  });

  // Update own take percentage mutation
  const updateOwnTakeMutation = useMutation({
    mutationFn: async (ownTakePercentage: number) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-own-take`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ownTakePercentage })
      });
      
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to update own take percentage');
      }
      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Your take percentage updated successfully!', { variant: 'success' });
      setShowEditOwnTakeDialog(false);
      queryClient.invalidateQueries({ queryKey: ['my-hierarchical-commission'] });
      queryClient.invalidateQueries({ queryKey: ['my-hierarchical-downlines'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  // Update upline take mutation
  const updateUplineTakeMutation = useMutation({
    mutationFn: async (data: { targetUserId: number; takePercentage: number }) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/upline-take`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to update upline take');
      }
      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Upline take updated successfully!', { variant: 'success' });
      setShowEditUplineTakeDialog(false);
      setSelectedDownlineForTake(null);
      queryClient.invalidateQueries({ queryKey: ['my-hierarchical-downlines'] });
      queryClient.invalidateQueries({ queryKey: ['my-upline-takes'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  // Fetch operators function
  const fetchOperators = async () => {
    try {
      setLoadingOperators(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/operator-commission-setup`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOperators(data);
        
        // Initialize operator commissions with default values
        const initialCommissions = data.map((operator: OperatorSetup) => ({
          operatorId: operator.operatorId,
          operatorCode: operator.operatorCode,
          operatorName: operator.operatorName,
          maxAllowedPercentage: 0.6, // 60%
          initialTakePercentage: 0, // 0%
          notes: ''
        }));
        
        setOperatorCommissions(initialCommissions);
      }
    } catch (error) {
      console.error('Error fetching operators:', error);
      enqueueSnackbar('Failed to load operators', { variant: 'error' });
    } finally {
      setLoadingOperators(false);
    }
  };

  // Create downline mutation
  const createDownlineMutation = useMutation({
    mutationFn: async (data: CreateDownlineRequest) => {
      const requestData = {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        phoneNumber: data.phoneNumber,
        password: data.password,
        maxAllowedPercentage: 0.6, // Default 60% for general settings
        initialTakePercentage: 0, // Default 0% for general settings
        canCreateDownlines: data.canCreateDownlines,
        notes: data.notes,
        operatorCommissions: operatorCommissions.map(op => ({
          operatorId: op.operatorId,
          operatorCode: op.operatorCode,
          operatorName: op.operatorName,
          maxAllowedPercentage: op.maxAllowedPercentage,
          initialTakePercentage: op.initialTakePercentage,
          notes: op.notes
        }))
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/create-downline-with-operators`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to create downline');
      }
      return result;
    },
    onSuccess: (result) => {
      enqueueSnackbar(`Downline "${result.fullName}" created successfully with ${result.operatorCommissionsCreated} operator commissions!`, { variant: 'success' });
      setShowCreateDialog(false);
      setCreateForm({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        phoneNumber: '',
        password: '',
        maxAllowedPercentage: 0.5,
        initialTakePercentage: 0.05,
        canCreateDownlines: true,
        notes: ''
      });
      // Reset operator commissions to defaults
      const resetCommissions = operators.map(operator => ({
        operatorId: operator.operatorId,
        operatorCode: operator.operatorCode,
        operatorName: operator.operatorName,
        maxAllowedPercentage: 0.6,
        initialTakePercentage: 0,
        notes: ''
      }));
      setOperatorCommissions(resetCommissions);
      queryClient.invalidateQueries({ queryKey: ['my-hierarchical-downlines'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  // Format amount per set (e.g., 0.50 → "RM 0.50")
  const formatAmount = (value: number) => formatCommissionAmount(value);
  // Legacy - for backward compatibility
  const formatPercentage = (value: number) => formatCommissionAmount(value);

  const handleOperatorCommissionChange = (operatorId: number, field: keyof OperatorCommission, value: any) => {
    setOperatorCommissions(prev => 
      prev.map(op => 
        op.operatorId === operatorId 
          ? { ...op, [field]: field === 'notes' ? value : value }
          : op
      )
    );
  };


  // Handle operator commission dialog open
  const handleOperatorCommissionDialogOpen = () => {
    setEditingOperatorCommissions([...myOperatorCommissionsData]);
    setShowOperatorCommissionDialog(true);
  };

  // Fetch downline operator commissions
  const fetchDownlineOperatorCommissions = async (downlineId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/downline-operator-commissions/${downlineId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDownlineOperatorCommissions(prev => ({
          ...prev,
          [downlineId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching downline operator commissions:', error);
    }
  };

  // Function to handle editing downline operator commissions
  const handleEditDownlineCommission = (downlineId: number, operatorId: number, operatorName: string) => {
    setShowEditDialog({ downlineId, operatorId, operatorName });
  };

  // Function to save downline operator commission changes
  const saveDownlineCommissionChanges = async (downlineId: number, operatorId: number, maxAllowed: number, ownTake: number, notes?: string) => {
    try {
      // Here you would call the API to update the downline operator commission
      // For now, we'll update the local state
      setDownlineOperatorCommissions(prev => ({
        ...prev,
        [downlineId]: prev[downlineId]?.map(op => 
          op.operatorId === operatorId 
            ? { ...op, maxAllowedPercentage: maxAllowed, ownTakePercentage: ownTake, notes } // Already in RM, no division
            : op
        ) || []
      }));
      
      setShowEditDialog(null);
      
      // TODO: Add API call to save changes
      console.log(`Saving changes for downline ${downlineId}, operator ${operatorId}:`, {
        maxAllowed: maxAllowed, // Already in RM
        ownTake: ownTake, // Already in RM
        notes
      });
      
    } catch (error) {
      console.error('Error saving downline commission changes:', error);
    }
  };

  // Handle operator commission change in edit dialog
  const handleEditOperatorCommissionChange = (operatorId: number, field: string, value: any) => {
    setEditingOperatorCommissions(prev => 
      prev.map(op => 
        op.operatorId === operatorId 
          ? { ...op, [field]: value }
          : op
      )
    );
  };

  // Update operator commissions
  const updateOperatorCommissionsMutation = useMutation({
    mutationFn: async (requestData: any) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-operator-commissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update operator commissions');
      }

      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Operator commissions updated successfully', { variant: 'success' });
      setShowOperatorCommissionDialog(false);
      queryClient.invalidateQueries({ queryKey: ['my-operator-commissions'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleUpdateOperatorCommissions = () => {
    const requestData = {
      userId: auth.user?.id,
      operatorCommissions: editingOperatorCommissions.map(op => ({
        operatorId: op.operatorId,
        ownTakePercentage: op.ownTakePercentage,
        maxAllowedPercentage: op.maxAllowedPercentage,
        notes: op.notes || ''
      }))
    };

    updateOperatorCommissionsMutation.mutate(requestData);
  };

  const handleCreateDialogOpen = () => {
    setShowCreateDialog(true);
    fetchOperators();
  };

  const handleEditOwnTake = () => {
    if (!myCommission) return;
    setEditOwnTakeForm({ ownTakePercentage: myCommission.ownTakePercentage });
    setEditingOwnTakeValue(myCommission.ownTakePercentage.toFixed(2)); // Already in RM, no multiplication
    setShowEditOwnTakeDialog(true);
  };

  // Optimized handler for own take input with real-time validation
  const handleOwnTakeChange = useCallback((value: string) => {
    if (!myCommission) return;
    const maxValue = myCommission.maxAllowedPercentage;
    const validatedValue = validateNumericInput(value, maxValue, true, 2);
    setEditingOwnTakeValue(validatedValue);
  }, [myCommission]);

  const handleEditUplineTake = (downline: HierarchicalCommission) => {
    const currentTake = myUplineTakes.find(ut => ut.targetUserId === downline.userId);
    setSelectedDownlineForTake(downline);
    setEditUplineTakeForm({
      targetUserId: downline.userId,
      takePercentage: currentTake?.takePercentage || 0
    });
    setEditingUplineTakeValue((currentTake?.takePercentage || 0).toFixed(2)); // Already in RM, no multiplication
    setShowEditUplineTakeDialog(true);
  };

  // Optimized handler for upline take input with real-time validation
  const handleUplineTakeChange = useCallback((value: string) => {
    if (!selectedDownlineForTake) return;
    const currentTake = myUplineTakes.find(ut => ut.targetUserId === selectedDownlineForTake.userId);
    const maxValue = selectedDownlineForTake.availableForUplines + (currentTake?.takePercentage || 0);
    const validatedValue = validateNumericInput(value, maxValue, true, 2);
    setEditingUplineTakeValue(validatedValue);
  }, [selectedDownlineForTake, myUplineTakes]);

  const handleShowBreakdown = (downline: HierarchicalCommission) => {
    setSelectedBreakdownUser(downline);
    setShowBreakdownDialog(true);
  };

  const toggleRowExpansion = (userId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  if (loadingMyCommission) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!myCommission) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hierarchical Commission Setup Required
          </Typography>
          <Typography>
            You don't have hierarchical commission setup yet. Please contact your administrator to set up your commission structure.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Container maxWidth={false} sx={{ mt: 4, mb: 2, px: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <AccountTreeIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Hierarchical Commission Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your commission structure with full control over your downline network
          </Typography>
        </Box>
      </Container>

      {/* Multi-Level Access - Full Width */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <MultiLevelCommissionManagement />
          </Box>
          
      {/* Dialogs - Keep functional for editing */}

      {/* Edit Own Take Dialog */}
      <Dialog 
        open={showEditOwnTakeDialog} 
        onClose={() => {
          setShowEditOwnTakeDialog(false);
          setEditingOwnTakeValue('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            Edit My Take Percentage
          </Box>
        </DialogTitle>
        <DialogContent>
          {myCommission && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Your Settings:</strong><br />
                • You can take up to <strong>{formatPercentage(myCommission.maxAllowedPercentage)}</strong> from your own sales<br />
                • Currently taking: <strong>{formatPercentage(myCommission.ownTakePercentage)}</strong><br />
                • Available for uplines: <strong>{formatPercentage(myCommission.availableForUplines)}</strong>
              </Typography>
            </Alert>
            
            <Typography variant="h6" gutterBottom color="primary.main">
              💰 My Take from My Sales
            </Typography>
            <TextField
              key="own-take-edit"
              fullWidth
              label="My Take Amount (RM/set)"
              type="text"
              value={editingOwnTakeValue || myCommission.ownTakePercentage.toFixed(2)}
              onChange={(e) => {
                // Real-time validation with max value constraint
                handleOwnTakeChange(e.target.value);
              }}
              onBlur={(e) => {
                // Validate and clamp when user leaves the field
                const maxValue = myCommission.maxAllowedPercentage; // Already in RM, no multiplication
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  const clamped = Math.max(0, Math.min(maxValue, value));
                  setEditingOwnTakeValue(clamped.toFixed(2));
                  setEditOwnTakeForm({ ownTakePercentage: clamped }); // Send amount directly (no division)
                } else {
                  setEditingOwnTakeValue('0.00');
                  setEditOwnTakeForm({ ownTakePercentage: 0 });
                }
              }}
              sx={{ mb: 3 }}
              helperText={`Max ${formatAmount(myCommission.maxAllowedPercentage)}/set`}
            />
            
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  💵 Breakdown (for every $100 I sell):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="success.contrastText">
                    🟢 I keep (per set): {formatAmount(editOwnTakeForm.ownTakePercentage)}
                  </Typography>
                  <Typography variant="body2" color="info.main">
                    🔵 Available for uplines (per set): {formatAmount(myCommission.maxAllowedPercentage - editOwnTakeForm.ownTakePercentage)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setShowEditOwnTakeDialog(false)}
            size="large"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => updateOwnTakeMutation.mutate(editOwnTakeForm.ownTakePercentage)}
            variant="contained"
            size="large"
            disabled={updateOwnTakeMutation.isPending}
            startIcon={updateOwnTakeMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updateOwnTakeMutation.isPending ? 'Updating...' : 'Update My Take'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Upline Take Dialog */}
      <Dialog 
        open={showEditUplineTakeDialog} 
        onClose={() => {
          setShowEditUplineTakeDialog(false);
          setEditingUplineTakeValue('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="warning" />
            Edit My Take from Downline
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDownlineForTake && (
            <Box sx={{ mt: 2 }}>
              <Card sx={{ mb: 3, bgcolor: 'primary.light' }}>
                <CardContent>
                  <Typography variant="h6" color="primary.contrastText" gutterBottom>
                    {selectedDownlineForTake.fullName} (@{selectedDownlineForTake.username})
                  </Typography>
                  <Typography variant="body2" color="primary.contrastText">
                    Editing how much I take from their sales
                  </Typography>
                </CardContent>
              </Card>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Their Settings:</strong><br />
                  • They can take up to: <strong>{formatPercentage(selectedDownlineForTake.maxAllowedPercentage)}</strong><br />
                  • They currently take: <strong>{formatPercentage(selectedDownlineForTake.ownTakePercentage)}</strong><br />
                  • Available for uplines: <strong>{formatPercentage(selectedDownlineForTake.availableForUplines)}</strong>
                </Typography>
              </Alert>
              
              <Typography variant="h6" gutterBottom color="warning.main">
                💰 My Take from Their Sales
              </Typography>
              <TextField
                key={`upline-take-${selectedDownlineForTake.userId}`}
                fullWidth
                label="My Take Amount (RM/set)"
                type="text"
                value={editingUplineTakeValue || (myUplineTakes.find(ut => ut.targetUserId === selectedDownlineForTake.userId)?.takePercentage || 0).toFixed(2)}
                onChange={(e) => {
                  // Real-time validation with max value constraint
                  handleUplineTakeChange(e.target.value);
                }}
                onBlur={(e) => {
                  // Validate and clamp when user leaves the field
                  const maxValue = selectedDownlineForTake.availableForUplines + (myUplineTakes.find(ut => ut.targetUserId === selectedDownlineForTake.userId)?.takePercentage || 0); // Already in RM, no multiplication
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    const clamped = Math.max(0, Math.min(maxValue, value));
                    setEditingUplineTakeValue(clamped.toFixed(2));
                    setEditUplineTakeForm({ ...editUplineTakeForm, takePercentage: clamped }); // Send amount directly (no division)
                  } else {
                    setEditingUplineTakeValue('0.00');
                    setEditUplineTakeForm({ ...editUplineTakeForm, takePercentage: 0 });
                  }
                }}
                sx={{ mb: 3 }}
                helperText={`Max ${formatAmount(selectedDownlineForTake.availableForUplines + (myUplineTakes.find(ut => ut.targetUserId === selectedDownlineForTake.userId)?.takePercentage || 0))}/set`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setShowEditUplineTakeDialog(false)}
            size="large"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => updateUplineTakeMutation.mutate(editUplineTakeForm)}
            variant="contained"
            size="large"
            color="warning"
            disabled={updateUplineTakeMutation.isPending}
            startIcon={updateUplineTakeMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updateUplineTakeMutation.isPending ? 'Updating...' : 'Update My Take'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default HierarchicalCommissionManagement;
